/* eslint-disable prettier/prettier */
import { useState, useCallback, useEffect } from 'react';
import {
	CourseBatch,
	courseService,
	DuplicateCourseRequest,
} from '../services/courseService';
import {
	CourseSummary,
	CourseDetails,
	CourseFilters,
	EnrollmentSummary,
	Solution,
	AddCoachRequest,
	CreateCourseRequest,
} from '../types/course';

// Hook for Catalog/Listings
export const useCourseCatalog = (initialFilters?: CourseFilters) => {
	const [courses, setCourses] = useState<CourseSummary[]>([]);
	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		limit: 20,
	});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchCatalog = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await courseService.getCoursesCatalog(initialFilters);
			// Ensure we handle the specific structure your backend returns
			if (data && data.courses) {
				setCourses(data.courses);
			} else if (Array.isArray(data)) {
				// In case your backend returns a direct array instead of an object
				setCourses(data);
			}
		} catch (err: any) {
			setError(err.message || 'An error occurred');
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchCatalog();
	}, [fetchCatalog]);

	return { courses, isLoading, error, refreshCatalog: fetchCatalog };
};
// Hook for a Single Course Details
export const useCourseDetails = (courseId: string, userId?: string) => {
	const [course, setCourse] = useState<CourseDetails | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchDetails = useCallback(async () => {
		if (!courseId) return;
		setIsLoading(true);
		try {
			const data = await courseService.getCourseDetails(courseId, userId);
			setCourse(data);
		} catch (err: any) {
			setError(err.message);
		} finally {
			setIsLoading(false);
		}
	}, [courseId, userId]);

	useEffect(() => {
		fetchDetails();
	}, [fetchDetails]);

	return { course, isLoading, error, refreshDetails: fetchDetails };
};

export const useCourseWithResources = (courseId: string, userId?: string) => {
	console.log('Hook triggered with ID:', courseId); // Check if this shows up
	const [courseWithResources, setCourseWithResources] =
		useState<CourseDetails | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchDetailsWithResources = useCallback(async () => {
		console.log('fetchDetailsWithResources called. ID is:', courseId);
		if (!courseId) {
			console.warn(
				'fetchDetailsWithResources aborted: No courseId provided'
			);
			return;
		}
		setIsLoading(true);
		setError(null);
		try {
			const data = await courseService.getCourseDetailsWithResources(
				courseId,
				userId
			);
			setCourseWithResources(data);
		} catch (err: any) {
			setError(err.message || 'An error occurred');
		} finally {
			setIsLoading(false);
		}
	}, [courseId, userId]);

	useEffect(() => {
		fetchDetailsWithResources();
	}, [fetchDetailsWithResources]);

	return {
		courseWithResources,
		isLoading,
		error,
		refreshDetails: fetchDetailsWithResources,
	};
};
export const useCourseWithModulesAndSessions = (courseId: string) => {
	const [courseData, setCourseData] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchCourseData = useCallback(async () => {
		if (!courseId) return;

		setIsLoading(true);
		setError(null);

		try {
			const data =
				await courseService.getCourseWithModulesAndSessions(courseId);
			setCourseData(data);
		} catch (err: any) {
			setError(err.message || 'An error occurred');
		} finally {
			setIsLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		fetchCourseData();
	}, [fetchCourseData]);

	return { courseData, isLoading, error, refresh: fetchCourseData };
};

// Hook for Coach's Own Programs (Created By Logged-in Coach)
export const useMyPrograms = (
	initialPage: number = 1,
	initialLimit: number = 20
) => {
	const [courses, setCourses] = useState<CourseSummary[]>([]);
	const [pagination, setPagination] = useState({
		total: 0,
		page: initialPage,
		limit: initialLimit,
	});

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchMyPrograms = useCallback(
		async (page = pagination.page, limit = pagination.limit) => {
			setIsLoading(true);
			setError(null);

			try {
				const data = await courseService.getMyPrograms(page, limit);

				if (data?.courses) {
					setCourses(data.courses);
					setPagination({
						total: data.total,
						page,
						limit,
					});
				} else {
					setCourses([]);
				}
			} catch (err: any) {
				setError(err.message || 'Failed to fetch your programs');
			} finally {
				setIsLoading(false);
			}
		},
		[pagination.page, pagination.limit]
	);

	useEffect(() => {
		fetchMyPrograms(initialPage, initialLimit);
	}, []);

	return {
		courses,
		pagination,
		isLoading,
		error,
		refreshMyPrograms: fetchMyPrograms,
	};
};
export const useAddFacultyToProgram = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const addFaculty = useCallback(
		async (
			courseId: string,
			payload: {
				userid: string;
				facultyrole?: string;
				displayorder?: number;
			}
		) => {
			setIsLoading(true);
			setError(null);
			setSuccess(false);

			try {
				const data = await courseService.addFacultyToProgram(
					courseId,
					payload
				);
				setSuccess(true);
				return data;
			} catch (err: any) {
				setError(err.message || 'Failed to add faculty');
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	return {
		addFaculty,
		isLoading,
		error,
		success,
	};
};

export const useSolutions = () => {
	const [solutions, setSolutions] = useState<Solution[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchSolutions = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const data = await courseService.getSolutions();
			setSolutions(data || []);
		} catch (err: any) {
			setError(err.message || 'Failed to fetch solutions');
		} finally {
			setIsLoading(false);
		}
	}, []);

	const createSolution = async (payload: {
		title: string;
		description?: string;
	}) => {
		setIsLoading(true);
		setError(null);

		try {
			const newSolution = await courseService.createSolution(payload);

			// optimistic update
			setSolutions(prev => [newSolution, ...prev]);

			return newSolution;
		} catch (err: any) {
			setError(err.message || 'Failed to create Offering');
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchSolutions();
	}, [fetchSolutions]);

	return {
		solutions,
		isLoading,
		error,
		refreshSolutions: fetchSolutions,
		createSolution,
	};
};

export const useCoachManagement = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const addCoach = async (courseId: string, payload: AddCoachRequest) => {
		setIsLoading(true);
		try {
			return await courseService.addCoach(courseId, payload);
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	const removeCoach = async (courseId: string, coachId: string) => {
		setIsLoading(true);
		try {

			console.log('HOOK REMOVE COACH');
			console.log('courseId', courseId);
			console.log('coachId', coachId);

			return await courseService.removeCoach(courseId, coachId);
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		addCoach,
		removeCoach,
		isLoading,
		error,
	};
};

export const useCreateCourse = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const createCourse = async (payload: CreateCourseRequest) => {
		setIsLoading(true);
		setError(null);

		try {
			const res = await courseService.createCourse(payload);
			return res;
		} catch (err: any) {
			setError(err.message || 'Failed to create course');
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		createCourse,
		isLoading,
		error,
	};
};

export const useUploadCover = () => {
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const uploadCover = async (courseId: string, file: File) => {
		setIsUploading(true);
		setError(null);

		try {
			const res = await courseService.uploadCover(courseId, file);
			return res;
		} catch (err: any) {
			setError(err.message || 'Failed to upload cover');
			throw err;
		} finally {
			setIsUploading(false);
		}
	};

	return {
		uploadCover,
		isUploading,
		error,
	};
};

export const useDuplicateCourse = () => {
	const [loading, setLoading] = useState(false);
	const [batches, setBatches] = useState<CourseBatch[]>([]);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Duplicate Course
	 */
	const duplicateCourse = async (
		courseId: string,
		payload: DuplicateCourseRequest
	) => {
		try {
			setLoading(true);
			setError(null);

			const finalPayload: DuplicateCourseRequest = {
				copyAssessments: false,
				copyResources: false,
				...payload,
			};

			const res = await courseService.duplicateCourse(
				courseId,
				finalPayload
			);

			if (!res.success) {
				throw new Error(res.message);
			}

			return res.data;
		} catch (err: any) {
			setError(err.message || 'Failed to duplicate program');
			throw err;
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Fetch Batches
	 */
	const fetchBatches = async (courseId: string) => {
		try {
			setLoading(true);
			setError(null);

			const res = await courseService.getCourseBatches(courseId);

			if (!res.success) {
				throw new Error(res.message);
			}

			setBatches(res.data || []);
		} catch (err: any) {
			setError(err.message || 'Failed to fetch batches');
		} finally {
			setLoading(false);
		}
	};

	return {
		loading,
		error,
		batches,
		duplicateCourse,
		fetchBatches,
	};
};

export const useRemoveFacultyFromProgram = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const removeFaculty = useCallback(
		async (courseId: string, userId: string) => {
			setIsLoading(true);
			setError(null);
			setSuccess(false);

			try {
				const data = await courseService.removeFacultyFromProgram(
					courseId,
					userId
				);

				setSuccess(true);
				return data;
			} catch (err: any) {
				setError(err.message || 'Failed to remove faculty');
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	return {
		removeFaculty,
		isLoading,
		error,
		success,
	};
};
export const useRemoveProgramOwner = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const removeOwner = useCallback(async (courseId: string) => {
		setIsLoading(true);
		setError(null);
		setSuccess(false);

		try {
			const data = await courseService.removeProgramOwner(courseId);
			setSuccess(true);
			return data;
		} catch (err: any) {
			setError(err.message || 'Failed to remove program owner');
			throw err;
		} finally {
			setIsLoading(false);
		}
	}, []);

	return {
		removeOwner,
		isLoading,
		error,
		success,
	};
};
export const useCoachProfilePrograms = (coachId: string) => {
	const [data, setData] = useState<any>(null);
	const [programs, setPrograms] = useState<any[]>([]);
	const [summary, setSummary] = useState<any>(null);

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchCoachPrograms = useCallback(async () => {
		if (!coachId) return;

		setIsLoading(true);
		setError(null);

		try {
			const res = await courseService.getCoachProfilePrograms(coachId);

			if (res) {
				setData(res);
				setPrograms(res.programs || []);
				setSummary(res.summary || null);
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch coach programs');
		} finally {
			setIsLoading(false);
		}
	}, [coachId]);

	useEffect(() => {
		fetchCoachPrograms();
	}, [fetchCoachPrograms]);

	return {
		data,
		programs,
		summary,
		isLoading,
		error,
		refresh: fetchCoachPrograms,
	};
};
export const useLearnerProfilePrograms = (learnerId: string) => {
	const [data, setData] = useState<any>(null);
	const [programs, setPrograms] = useState<any[]>([]);
	const [summary, setSummary] = useState<any>(null);

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchLearnerPrograms = useCallback(async () => {
		if (!learnerId) return;

		setIsLoading(true);
		setError(null);

		try {
			const res =
				await courseService.getLearnerProfilePrograms(learnerId);

			if (res) {
				setData(res);
				setPrograms(res.programs || []);
				setSummary(res.summary || null);
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch learner programs');
		} finally {
			setIsLoading(false);
		}
	}, [learnerId]);

	useEffect(() => {
		fetchLearnerPrograms();
	}, [fetchLearnerPrograms]);

	return {
		data,
		programs,
		summary,
		isLoading,
		error,
		refresh: fetchLearnerPrograms,
	};
};
